import {
	Scene,
	Vector3,
	MeshBuilder,
	StandardMaterial,
	Color3,
	TransformNode,
} from "@babylonjs/core";

/**
 * Tiny "industrial-park worker" NPCs. Each picks a random waypoint, walks
 * toward it, then picks another. No collision, no pathfinding — just wander.
 * Cheap visual ambient population.
 */

const WAYPOINTS: Vector3[] = [
	new Vector3(-18, 0, -14), // shop area
	new Vector3(16, 0, -14),  // kiosk area
	new Vector3(-30, 0, -4),  // west of warehouse
	new Vector3(30, 0, -4),   // east of warehouse
	new Vector3(0, 0, 22),    // loading dock
	new Vector3(-12, 0, 22),
	new Vector3(12, 0, 22),
	new Vector3(-22, 0, 25),
	new Vector3(22, 0, 25),
	new Vector3(0, 0, -4),    // road south
];

const SKIN_COLORS = [
	new Color3(0.95, 0.85, 0.75),
	new Color3(0.7, 0.55, 0.4),
	new Color3(0.55, 0.4, 0.3),
	new Color3(0.95, 0.8, 0.65),
];

const SHIRT_COLORS = [
	new Color3(0.8, 0.2, 0.2),
	new Color3(0.2, 0.4, 0.7),
	new Color3(0.95, 0.7, 0.2),
	new Color3(0.3, 0.6, 0.3),
	new Color3(0.6, 0.3, 0.7),
	new Color3(0.95, 0.95, 0.95),
];

const PANTS_COLOR = new Color3(0.18, 0.22, 0.32);

interface NPC {
	root: TransformNode;
	leftLeg: TransformNode;
	rightLeg: TransformNode;
	leftArm: TransformNode;
	rightArm: TransformNode;
	target: Vector3;
	speed: number;
	walkPhase: number;
}

const HAIR_COLORS = [
	new Color3(0.08, 0.05, 0.03), // black
	new Color3(0.25, 0.13, 0.06), // dark brown
	new Color3(0.5, 0.32, 0.16),  // medium brown
	new Color3(0.85, 0.65, 0.3),  // blonde
	new Color3(0.55, 0.2, 0.1),   // red/auburn
	new Color3(0.65, 0.65, 0.7),  // gray (older)
];

function buildNpc(scene: Scene, id: number): NPC {
	const root = new TransformNode(`npc-${id}`, scene);

	const skin = SKIN_COLORS[Math.floor(Math.random() * SKIN_COLORS.length)]!;
	const shirt = SHIRT_COLORS[Math.floor(Math.random() * SHIRT_COLORS.length)]!;
	const hair = HAIR_COLORS[Math.floor(Math.random() * HAIR_COLORS.length)]!;
	const heightScale = 0.92 + Math.random() * 0.16; // 0.92–1.08, gives variety
	const torsoWidth = 0.42 + Math.random() * 0.08;
	const isWide = Math.random() < 0.25;

	const skinMat = new StandardMaterial(`npc-skin-${id}`, scene);
	skinMat.diffuseColor = skin;
	const shirtMat = new StandardMaterial(`npc-shirt-${id}`, scene);
	shirtMat.diffuseColor = shirt;
	const pantsMat = new StandardMaterial(`npc-pants-${id}`, scene);
	pantsMat.diffuseColor = PANTS_COLOR;
	const hairMat = new StandardMaterial(`npc-hair-${id}`, scene);
	hairMat.diffuseColor = hair;
	const eyeMat = new StandardMaterial(`npc-eye-${id}`, scene);
	eyeMat.diffuseColor = new Color3(0.08, 0.08, 0.1);

	root.scaling.y = heightScale;

	// Hips — a slightly narrower box under the torso for body shape
	const hips = MeshBuilder.CreateBox(`npc-hips-${id}`, {
		width: torsoWidth * 0.95,
		depth: 0.24,
		height: 0.18,
	}, scene);
	hips.position.y = 0.86;
	hips.material = pantsMat;
	hips.parent = root;

	// Torso — taller and tapered shoulders via a wider top box
	const torso = MeshBuilder.CreateBox(`npc-torso-${id}`, {
		width: torsoWidth + (isWide ? 0.1 : 0),
		depth: 0.26,
		height: 0.62,
	}, scene);
	torso.position.y = 1.28;
	torso.material = shirtMat;
	torso.parent = root;

	// Shoulders accent — slightly wider top of torso (one extra small box)
	const shoulders = MeshBuilder.CreateBox(`npc-shoulders-${id}`, {
		width: torsoWidth + 0.1,
		depth: 0.28,
		height: 0.08,
	}, scene);
	shoulders.position.y = 1.55;
	shoulders.material = shirtMat;
	shoulders.parent = root;

	// Neck stub
	const neck = MeshBuilder.CreateCylinder(`npc-neck-${id}`, {
		diameter: 0.12,
		height: 0.08,
		tessellation: 10,
	}, scene);
	neck.position.y = 1.65;
	neck.material = skinMat;
	neck.parent = root;

	const head = MeshBuilder.CreateSphere(`npc-head-${id}`, { diameter: 0.3, segments: 10 }, scene);
	head.position.y = 1.82;
	head.scaling.y = 1.1;
	head.material = skinMat;
	head.parent = root;

	// Hair as a slightly larger half-sphere on top
	const hairTop = MeshBuilder.CreateSphere(`npc-hair-${id}`, {
		diameter: 0.32,
		segments: 10,
		slice: 0.6,
	}, scene);
	hairTop.position.y = 1.86;
	hairTop.scaling.y = 0.8;
	hairTop.material = hairMat;
	hairTop.parent = root;

	// Two small eyes on the front of the head (looking down +Z by default — NPC faces target)
	for (const side of [-1, 1]) {
		const eye = MeshBuilder.CreateSphere(`npc-eye-${id}-${side}`, { diameter: 0.04, segments: 6 }, scene);
		eye.position.set(side * 0.06, 1.83, 0.14);
		eye.material = eyeMat;
		eye.parent = root;
	}

	const mkLimb = (name: string, isLeg: boolean, sideX: number) => {
		const pivot = new TransformNode(`${name}-pivot-${id}`, scene);
		pivot.parent = root;
		pivot.position.set(sideX, isLeg ? 0.86 : 1.5, 0);
		const limbWidth = isLeg ? 0.13 : 0.11;
		const limbLen = isLeg ? 0.78 : 0.6;
		const mesh = MeshBuilder.CreateBox(`${name}-${id}`, { width: limbWidth, depth: limbWidth, height: limbLen }, scene);
		mesh.position.y = -limbLen / 2;
		mesh.material = isLeg ? pantsMat : shirtMat;
		mesh.parent = pivot;
		// Hand/shoe stub at the bottom
		const tip = MeshBuilder.CreateBox(`${name}-tip-${id}`, {
			width: limbWidth + 0.02,
			depth: limbWidth + 0.04,
			height: 0.07,
		}, scene);
		tip.position.y = -limbLen - 0.03;
		tip.material = isLeg ? new StandardMaterial(`shoe-${id}-${name}`, scene) : skinMat;
		if (isLeg) (tip.material as StandardMaterial).diffuseColor = new Color3(0.08, 0.06, 0.05);
		tip.parent = pivot;
		return pivot;
	};

	const leftLeg = mkLimb("npc-llg", true, -0.11);
	const rightLeg = mkLimb("npc-rlg", true, 0.11);
	const leftArm = mkLimb("npc-larm", false, -(torsoWidth / 2 + 0.05));
	const rightArm = mkLimb("npc-rarm", false, torsoWidth / 2 + 0.05);

	const start = WAYPOINTS[Math.floor(Math.random() * WAYPOINTS.length)]!;
	root.position.copyFrom(start);

	return {
		root,
		leftLeg,
		rightLeg,
		leftArm,
		rightArm,
		target: WAYPOINTS[Math.floor(Math.random() * WAYPOINTS.length)]!.clone(),
		speed: 0.7 + Math.random() * 0.4,
		walkPhase: Math.random() * Math.PI * 2,
	};
}

export interface NpcCrowd {
	tick: (dt: number) => void;
	dispose: () => void;
}

export function spawnNpcCrowd(scene: Scene, count: number): NpcCrowd {
	const npcs: NPC[] = [];
	for (let i = 0; i < count; i++) npcs.push(buildNpc(scene, i));

	const tick = (dt: number) => {
		for (const n of npcs) {
			const dx = n.target.x - n.root.position.x;
			const dz = n.target.z - n.root.position.z;
			const dist = Math.sqrt(dx * dx + dz * dz);
			if (dist < 0.6) {
				n.target = WAYPOINTS[Math.floor(Math.random() * WAYPOINTS.length)]!.clone();
				continue;
			}
			const step = n.speed * dt;
			n.root.position.x += (dx / dist) * step;
			n.root.position.z += (dz / dist) * step;
			n.root.rotation.y = Math.atan2(dx, dz);

			// Animate legs/arms with a sine wave
			n.walkPhase += dt * 6;
			const swing = Math.sin(n.walkPhase) * 0.5;
			n.leftLeg.rotation.x = swing;
			n.rightLeg.rotation.x = -swing;
			n.leftArm.rotation.x = -swing * 0.7;
			n.rightArm.rotation.x = swing * 0.7;
		}
	};

	const dispose = () => {
		for (const n of npcs) n.root.dispose();
	};

	return { tick, dispose };
}
